import { Component, ViewEncapsulation } from '@angular/core';
import { BaseBPWizardFormComponent } from '../base-bp-wizard-form.component';
import { TransitionService } from '@uirouter/angular';
import { BPWizardService } from '../bp-wizard.service';
import { Override } from '../../../../shared/app-decorators';
import { BPProfilesOEMDataService, IOEMDistributor, IOEMRelation, IOEMRelationExtended, OEMTierStatus } from '../../advanced/oem/bp-profiles-oem-data.service';

import { util } from '../../../../shared/util';
import * as _ from 'underscore';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../shared/formly/ga-formly.service';
import { ICompanyCountryExtended, ILov, IObject } from '../../../../../type_definitions/datatypes';
import { LOV_TIER_STATUS, LovService } from '../../../../shared/services/lov.service';
import { BPProfilesOEMService } from '../../advanced/oem/bp-profiles-oem.service';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { DialogSize, GaMessageService, IDialog } from '../../../../shared/message/ga-message.service';
import { IOEMTierStatusDialogModel, OEMTierStatusDialogComponent } from '../../advanced/oem/dialogs/oem-tier-status-dialog.component';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { BPProfilesService } from '../../bp-profiles.service';
import { BPProfilesDataService } from '../../bp-profiles-data.service';

interface IBPWizardOEMModel {
  oemRelations?:IOEMRelationExtended[];
}

@Component({
  selector: 'bp-wizard-oem',
  template: `
    <h2 [class.step-complete]="stepComplete"><text key="HEADING"></text></h2>
    <text key="INFO"></text>
    
    <h3 *ngIf="loading"><activity-indicator [useDefaultText]="true"></activity-indicator></h3>
    
    <div class="d-block margin-top-md" *ngIf="!loading" >
      <formly [form]="form" [model]="model" [fields]="fields"></formly>
      <fixed-to-bottom innerClass="fixed-to-bottom">
        <button class="btn btn-primary float-end" (click)="newOEM()" translate [translateParams]="{ label: 'BPPROFILES.OEM.OEM.LABEL' | translate }">GA.BUTTONS.NEW_LABEL</button>  
      </fixed-to-bottom>
    </div>
  `,
  styleUrls: ['./bp-wizard-oem.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPWizardOEMComponent extends BaseBPWizardFormComponent {
  public model:IBPWizardOEMModel = {};
  private tierStatuses:ILov[];
  private distributors:ICompanyCountryExtended[];

  constructor(
    private translationService:TranslationService,
    private messageService:GaMessageService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private bpProfilesOEMDataService:BPProfilesOEMDataService,
    private lovService:LovService,
    private bpProfilesOEMService:BPProfilesOEMService,
    protected transitionService:TransitionService,
    protected bpWizardService:BPWizardService,
    protected bpProfilesDataService:BPProfilesDataService,
    bpProfilesService:BPProfilesService
  ) {
    super(transitionService, bpWizardService, bpProfilesService, bpProfilesDataService);
  }

  @Override(BaseBPWizardFormComponent)
  public loadData():Promise<IOEMRelationExtended[]> {
    // if oem relations stored in service then resolve this one instead
    if (this.bpWizardService.oemRelations) {
      return Promise.resolve(this.updateOEMRelations(this.bpWizardService.oemRelations));
    }

    return this.bpProfilesOEMDataService.getOEMRelations(this.bpProfilesService.activeProfile?.id)
      .then(this.updateOEMRelations);
  }

  @Override(BaseBPWizardFormComponent)
  public onDataLoaded():Promise<void> {
    return Promise.all([
      this.lovService.list(LOV_TIER_STATUS),
      this.bpProfilesOEMDataService.getCountryDistributors()
    ]).then(util.spread((tierStatuses:ILov[], distributors:ICompanyCountryExtended[]) => {
      this.tierStatuses = tierStatuses;
      this.distributors = distributors;
      this.fields = this.createFields();
    }));
  }

  @Override(BaseBPWizardFormComponent)
  public get isFormValuesChange():boolean {
    let isOEMChange:boolean = false;
    _.each(this.model.oemRelations, (oemRelation:IOEMRelationExtended) => {
      const backupOEMRelation:IOEMRelationExtended = _.findWhere(this.bpWizardService.oemRelations, { id: oemRelation.id });
      const diff:IObject = util.diff(backupOEMRelation, oemRelation);
      if (!_.isEmpty(diff)) {
        isOEMChange = true;
      }
    });

    return this.form.dirty && isOEMChange;
  }

  private updateOEMRelations = (oemRelations:IOEMRelation[]):IOEMRelationExtended[] => {
    const formattedOEMRelations:IOEMRelationExtended[] = this.formatOEMRelations(oemRelations);
    this.bpWizardService.oemRelations = util.copyAndExtend(formattedOEMRelations);
    this.model.oemRelations = formattedOEMRelations;
    this.fields = this.createFields();
    return formattedOEMRelations;
  };

  private formatOEMRelations(oemRelations:IOEMRelation[]):IOEMRelationExtended[] {
    const oemRelationsFormatted:IOEMRelationExtended[] = [];

    _.each(oemRelations, (oemRelation:IOEMRelation) => {
      // set 'distributor' to the preferred one from 'distributors' (conversion from IOemRelationDistributor[] to ICompanyCountryExtended)
      const oemRelationFormatted:IOEMRelationExtended = util.copyAndExtend(oemRelation);
      if (oemRelationFormatted.tierStatus?.id === OEMTierStatus.DISTRIBUTOR) {
        const preferredDistributor:IOEMDistributor = _.findWhere(oemRelation.distributors, { preferred: true });
        oemRelationFormatted.distributor = oemRelationFormatted.distributor || preferredDistributor?.companyCountry;
      }
      oemRelationsFormatted.push(oemRelationFormatted);
    });

    return _.sortBy(oemRelationsFormatted, (oemRelation:IOEMRelationExtended) => oemRelation.oem?.name);
  }

  protected createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.anArray('oemRelations')
        .withoutWrapper()
        .withArrayFields([
          this.formlyService.aRadio('tierStatus')
            .withLabelClass(this.labelClass)
            .withValueClass(this.valueClass)
            .withGroupClass('mb-0')
            .withOptions(this.bpProfilesOEMService.processTierStatuses(this.tierStatuses))
            .withFormat(this.formattingService.formatLov)
            .withoutEditable()
            .withInline()
            .withExtraOptions({
              expressionProperties: {
                'templateOptions.label': (oemRelation:IOEMRelationExtended):string => oemRelation.oem.name
              }
            })
            .build(),
          this.formlyService.aSearch('distributor')
            .withoutEditable()
            .withLabelClass(this.labelClass)
            .withValueClass(this.valueClass)
            .withHideExpression((oemRelation:IOEMRelationExtended):boolean => oemRelation?.tierStatus?.id !== OEMTierStatus.DISTRIBUTOR)
            .withFormat(this.formattingService.formatCompanyCountryL)
            .withOptions(this.distributors)
            .withExtraOptions({
              templateOptions: {
                localSearch: true
              }
            })
            .build()
        ])
        .withFieldArrayWrapperClass('oem-group')
        .withExtraOptions({
          templateOptions: {
            showFieldArrayButtons: false
          }
        })
        .build()
    ];
  }

  public newOEM():void {
    const dialog:IDialog = this.messageService.showDialog({
      component: OEMTierStatusDialogComponent,
      wrapperComponent: false,
      size: DialogSize.MD,
      inputs: {
        adding: true,
        entity: 'BPPROFILES.OEM.OEM.LABEL',
        hideCommentsField: true
      },
      outputs: {
        onCancel: () => dialog.hide(),
        onSave: (model:IOEMTierStatusDialogModel) => {
          // if oem already existed and dialog mode is 'NEW' then do nothing
          const existedOEM:IOEMRelation = _.find(this.bpWizardService.oemRelations, (relation:IOEMRelation) => relation?.oem?.id === model.oem?.id);

          if (existedOEM) {
            this.messageService.showMessageDialog(
              this.translationService.instant('BPPROFILES.OEM.EXIST_OEM_TITLE'),
              this.translationService.instant('BPPROFILES.OEM.EXIST_OEM_MESSAGE', { oemName: model?.oem?.name }),
              undefined,
              undefined,
              undefined,
              DialogSize.MD
            );
            return dialog.hide();
          } else {
            dialog.content.inputs.saving = true;
            this.bpProfilesOEMDataService.addOEMRelation(this.bpProfilesService.activeProfile?.id, <IOEMRelation>{ oem: model.oem, tierStatus: model.tierStatus, comments: model.comments })
              .then((oemRelationResponse:IOEMRelation) => {
                if (model.tierStatus?.id === OEMTierStatus.DISTRIBUTOR) {
                  return this.bpProfilesOEMDataService.addNewDistributor(this.bpProfilesService.activeProfile?.id, oemRelationResponse.id, true, model.distributor)
                    .then((distributorResponse:IOEMDistributor) => {
                      oemRelationResponse.distributors = distributorResponse? [ distributorResponse ] : [];
                      return oemRelationResponse;
                    });
                }
                return oemRelationResponse;
              })
              .then(this.addNewOEMRelation)
              .finally(() => {
                dialog.content.inputs.saving = false;
                dialog.hide();
              });
          }
        }
      }
    });
  }

  private addNewOEMRelation = (oemRelation:IOEMRelation):void => {
    const sortByFn = (oemRelation:IOEMRelationExtended) => oemRelation.oem?.name;
    // add a new oem relation
    const formattedOEMRelation:IOEMRelationExtended = this.formatOEMRelations([oemRelation])[0];
    this.model.oemRelations.push(formattedOEMRelation);
    this.model.oemRelations = _.sortBy(this.model.oemRelations, sortByFn);
    this.fields = this.createFields();

    // the new OEM is added to the initial list
    // this is needed when there is a combination of changing OEM accreditations, adding new OEM and then saving
    this.bpWizardService.oemRelations.push(formattedOEMRelation);
    this.bpWizardService.oemRelations = _.sortBy(this.bpWizardService.oemRelations, sortByFn);
  };

  private getChangedOEMRelations():IOEMRelationExtended[] {
    const changedOEMRelations:IOEMRelationExtended[] = [];
    _.each(this.model.oemRelations, (oemRelation:IOEMRelationExtended) => {
      const backupOEMRelation:IOEMRelationExtended = _.findWhere(this.bpWizardService.oemRelations, { id: oemRelation.id });
      const diff:IObject = util.diff(backupOEMRelation, oemRelation);
      if (!_.isEmpty(diff)) {
        changedOEMRelations.push(oemRelation);
      }
    });
    return changedOEMRelations;
  }

  protected onSave = ():Promise<IOEMRelationExtended[] | void> => {
    let promise:Promise<void> = Promise.resolve();
    if (this.isFormValuesChange) {
      promise = <Promise<void>>this.bpProfilesOEMDataService.updateOEMRelations(this.bpProfilesService.activeProfile?.id, this.getChangedOEMRelations())
        .then((updatedOEMRelations:IOEMRelation[]) => {
          const oemRelations:IOEMRelationExtended[] = util.copyAndExtend(this.model.oemRelations);

          _.each(updatedOEMRelations, (updatedOEMRelation:IOEMRelationExtended) => {
            const idx:number = _.findIndex(oemRelations, (oemRelation:IOEMRelationExtended) => oemRelation.id === updatedOEMRelation.id);
            oemRelations[idx] = updatedOEMRelation;
          });

          this.updateOEMRelations(oemRelations);
        })
        .then(() => {
          this.form.markAsPristine();
        });
    }

    this.setSaveBusy(true);
    return promise
      .then(this.getProgress.bind(this))
      .finally(() => {
        this.setSaveBusy(false);
      });
  };
}